import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Keep original StatsCard unchanged for other uses
const StatsCard = ({ title, value, className = "", borderColor }) => {
  return (
    <div
      style={{
        boxShadow: " 0px 1.5px 27.6px 0px #E1D8FFC4",
        borderLeftColor: borderColor,
      }}
      className={`
        relative overflow-hidden border-l-4 hover:border-opacity-100 rounded-2xl p-6 transition-all duration-300 cursor-pointer group
        bg-[#FFFFFF] text-black 
        ${className}
      `}
    >
      <div className="relative z-10 text-center">
        <h3 className="text-base font-bold mb-2 text-black transition-colors duration-300">
          {title}
        </h3>
        <p className="text-3xl font-bold text-black transition-colors duration-300">
          {value}
        </p>
      </div>
    </div>
  );
};

// New Clickable Stats Card for navigation
const ClickableStatsCard = ({
  title,
  value,
  className = "",
  borderColor,
  onClick,
}) => {
  return (
    <div
      style={{
        boxShadow: " 0px 1.5px 27.6px 0px #E1D8FFC4",
        borderLeftColor: borderColor,
      }}
      className={`
        relative overflow-hidden border-l-4 hover:border-opacity-100 rounded-2xl p-6 transition-all duration-300 cursor-pointer group
        bg-[#FFFFFF] text-black hover:shadow-lg transform hover:scale-105
        ${className}
      `}
      onClick={onClick}
    >
      <div className="relative z-10 text-center">
        <h3 className="text-base font-bold mb-2 text-black transition-colors duration-300">
          {title}
        </h3>
        <p className="text-3xl font-bold text-black transition-colors duration-300">
          {value}
        </p>
      </div>
    </div>
  );
};

const DynamicStatsInterface = ({ contentStatistics }) => {
  const [activeTab, setActiveTab] = useState("galleries");
  const navigate = useNavigate();

  // Navigation function based on card type and active tab
  const handleCardClick = (cardTitle, tabKey) => {
    const routes = {
      galleries: {
        Approved: "/admin/gallery/list",
        "Not Approved": "/admin/gallery/pending",
        Total: "/admin/gallery/list",
      },
      news: {
        Approved: "/admin/news/list",
        "Not Approved": "/admin/news/pending",
        Total: "/admin/news/list",
      },
      events: {
        Approved: "/admin/events/list",
        "Not Approved": "/admin/events/pending",
        Total: "/admin/events/list",
      },
      jobs: {
        Active: "/admin/jobs/list",
        Closed: "/admin/jobs/list?status=closed",
        Total: "/admin/jobs/list",
      },
    };

    const route = routes[tabKey]?.[cardTitle];
    if (route) {
      navigate(route);
    }
  };

  // Handle loading state
  if (!contentStatistics) {
    return (
      <div className="w-full p-6 mt-4">
        <div className="animate-pulse">
          <div className="flex gap-12 mb-8">
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="h-6 bg-gray-300 rounded w-20"></div>
              ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="h-32 bg-gray-300 rounded-2xl"></div>
              ))}
          </div>
        </div>
      </div>
    );
  }

  const tabData = {
    galleries: {
      color: "#009E2794",
      stats: [
        {
          title: "Approved",
          value: contentStatistics.galleries?.approved || 0,
        },
        {
          title: "Not Approved",
          value: contentStatistics.galleries?.notApproved || 0,
        },
        { title: "Total", value: contentStatistics.galleries?.total || 0 },
      ],
    },
    news: {
      color: "#DADD1094",
      stats: [
        { title: "Approved", value: contentStatistics.news?.approved || 0 },
        {
          title: "Not Approved",
          value: contentStatistics.news?.notApproved || 0,
        },
        { title: "Total", value: contentStatistics.news?.total || 0 },
      ],
    },
    events: {
      color: "#DD10BB94",
      stats: [
        { title: "Approved", value: contentStatistics.events?.approved || 0 },
        {
          title: "Not Approved",
          value: contentStatistics.events?.notApproved || 0,
        },
        { title: "Total", value: contentStatistics.events?.total || 0 },
      ],
    },
    jobs: {
      color: "#10CCDD94",
      stats: [
        { title: "Active", value: contentStatistics.jobs?.approved || 0 },
        {
          title: "Closed",
          value: contentStatistics.jobs?.notApproved || 0,
        },
        { title: "Total", value: contentStatistics.jobs?.total || 0 },
      ],
    },
  };

  const tabs = [
    { key: "galleries", label: "Galleries" },
    { key: "news", label: "News" },
    { key: "events", label: "Events" },
    { key: "jobs", label: "Jobs" },
  ];

  return (
    <div className="w-full p-6 mt-4">
      <div className="flex gap-12 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`
              text-lg font-bold cursor-pointer border-b-2 transition-all duration-300
              ${
                activeTab === tab.key
                  ? "text-[#4F22D8] border-[#4F22D8]"
                  : "text-black border-transparent"
              }
            `}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6 rounded-3xl transition-all duration-500">
        {tabData[activeTab].stats.map((stat, index) => (
          <ClickableStatsCard
            key={`${activeTab}-${index}`}
            title={stat.title}
            value={stat.value}
            borderColor={tabData[activeTab].color}
            onClick={() => handleCardClick(stat.title, activeTab)}
          />
        ))}
      </div>
    </div>
  );
};

export default DynamicStatsInterface;
